import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Reveal, SectionLabel } from "@/components/Reveal";
import VideoPlayer from "@/components/VideoPlayer";
import SectionBg from "@/components/SectionBg";
import { scrollToId } from "@/lib/attribution";
import { HOST, ASSETS } from "@/data/content";
import { REPLAY, REPLAY_TICKER, RECAP_LINES, RECAP_PARA, REVIEW_STEPS, OFFER_STACK, GUARANTEE } from "@/data/replayContent";

gsap.registerPlugin(ScrollTrigger);

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

const MaskedLine = ({ children, i, className }) => (
  <span className="block overflow-hidden">
    <motion.span className={`block ${className || ""}`} custom={i} variants={line} initial="hidden" animate="show">
      {children}
    </motion.span>
  </span>
);

export const ReplayHero = () => (
  <section id="watch" className="relative min-h-screen grid lg:grid-cols-[45fr_55fr] items-center gap-8 px-6 sm:px-12 lg:px-16 pt-28 pb-16" data-testid="replay-hero-section">
    <SectionBg src={ASSETS.bg.luxHomeDusk} opacity={0.16} position="center 60%" />
    <div className="relative z-10">
      <p className="font-mono text-[10px] tracking-[0.25em] text-[#D9622B] mb-5">{REPLAY.eyebrow}</p>
      <h1 className="replay-h1" data-testid="replay-h1">
        <MaskedLine i={0}>{REPLAY.h1a}</MaskedLine>
        <MaskedLine i={1} className="text-[#D9622B]">{REPLAY.h1b}</MaskedLine>
        <MaskedLine i={2} className="opacity-60 text-[0.62em]">{REPLAY.h1c}</MaskedLine>
      </h1>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }} className="mt-6 text-[15px] text-white/55 max-w-xl leading-relaxed">{REPLAY.sub}</motion.p>
      <motion.blockquote initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.05, duration: 0.8 }} className="mt-6 border-l-2 border-[#D9622B] pl-4 italic text-sm text-white/60 max-w-lg">{REPLAY.blockquote}</motion.blockquote>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15, duration: 0.8 }} className="mt-6 font-mono text-[11px] tracking-[0.15em] text-[#D9622B]" data-testid="replay-availability">{REPLAY.availability}</motion.p>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.8 }} className="mt-10 flex items-center gap-4">
        <img src={ASSETS.headshot} alt={HOST.name} className="w-14 h-14 rounded-full border-2 border-[#D9622B] object-cover object-top" />
        <div>
          <p className="text-sm font-bold text-[#F0ECE4]">{HOST.name}</p>
          <p className="text-[11px] text-white/40">{HOST.company} · Broker NMLS# {HOST.companyNmls}</p>
        </div>
      </motion.div>
    </div>
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="relative z-10 w-full">
      <VideoPlayer />
      <button data-testid="replay-hero-cta" onClick={() => scrollToId("book")} className="mt-4 w-full py-4 btn-primary text-black font-black text-sm tracking-[0.1em]">
        ✓ BOOK MY FREE VA BENEFIT REVIEW
      </button>
      <p className="mt-2.5 text-center text-[10px] text-white/25">Free · No obligation · Educational review only</p>
    </motion.div>
  </section>
);

export const ReplayTicker = () => (
  <section className="border-y border-white/10 py-4 overflow-hidden" data-testid="replay-ticker">
    <div className="marquee font-mono text-[11px] tracking-[0.2em] text-white/40">
      {[0, 1].map((n) => (
        <div className="marquee-track" key={n} aria-hidden={n === 1}>
          {REPLAY_TICKER.map((t) => (
            <span key={t} className="mx-6 whitespace-nowrap">{t} <span className="text-[#D9622B] mx-6">◆</span></span>
          ))}
        </div>
      ))}
    </div>
  </section>
);

export const Recap = () => {
  const ref = useRef(null);
  useEffect(() => {
    const words = ref.current.querySelectorAll(".m-word");
    const tween = gsap.fromTo(words, { opacity: 0, y: 24 }, {
      opacity: 1, y: 0, stagger: 0.04, duration: 0.6, ease: "power3.out",
      scrollTrigger: { trigger: ref.current, start: "top 75%" },
    });
    return () => { tween.scrollTrigger?.kill(); tween.kill(); };
  }, []);

  return (
    <section className="relative px-6 sm:px-12 lg:px-16 py-28 lg:py-36" data-testid="recap-section">
      <SectionBg src={ASSETS.bg.veteranKeys} opacity={0.14} position="center 30%" />
      <div className="relative z-10">
        <SectionLabel num="001" text="WHAT YOU JUST LEARNED" />
        <h2 ref={ref} className="mt-8 recap-h max-w-5xl">
          {RECAP_LINES.map((l) => (
            <span key={l.text} className={`block ${l.cls}`}>
              {l.text.split(" ").map((w, i) => (
                <span key={i} className="m-word inline-block mr-[0.28em]">{w}</span>
              ))}
            </span>
          ))}
        </h2>
        <Reveal delay={0.15}>
          <p className="mt-10 text-white/55 text-base leading-[1.75] max-w-[560px]">{RECAP_PARA}</p>
          <button data-testid="recap-cta" onClick={() => scrollToId("book")} className="mt-8 px-7 py-3.5 btn-outline border border-[#D9622B] text-[#D9622B] font-bold text-sm tracking-[0.1em]">
            SEE HOW IT APPLIES TO YOU →
          </button>
        </Reveal>
      </div>
    </section>
  );
};

export const ReviewRoadmap = () => (
  <section id="review" className="border-y border-white/10" data-testid="review-roadmap-section">
    <div className="grid lg:grid-cols-3">
      {REVIEW_STEPS.map((s, i) => (
        <div key={s.num} className={`lift-card relative p-8 lg:p-12 overflow-hidden ${i > 0 ? "lg:border-l border-t lg:border-t-0 border-white/10" : ""}`} data-testid={`review-step-${i + 1}`}>
          <span className="absolute -top-6 -right-2 font-mono text-[140px] font-bold leading-none text-white/[0.04] select-none">{s.num}</span>
          <p className="font-mono text-[10px] tracking-[0.25em] text-[#D9622B]">STEP {s.num}</p>
          <h3 className="mt-3 text-2xl font-black uppercase" style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}>{s.name}</h3>
          <p className="mt-3 text-[13px] text-white/45 leading-relaxed max-w-sm">{s.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

export const OfferStack = () => (
  <section className="relative px-6 sm:px-12 lg:px-16 py-28" data-testid="offer-stack-section">
    <SectionBg src={ASSETS.bg.luxHomeEstate} opacity={0.12} />
    <div className="relative z-10 max-w-3xl mx-auto">
      <div className="text-center">
        <SectionLabel num="002" text="THE OFFER" />
        <Reveal><h2 className="mt-6 section-h">HERE'S WHAT'S WAITING WHEN YOU BOOK</h2></Reveal>
      </div>
      <Reveal delay={0.1}>
        <div className="mt-12 glass-card p-8 sm:p-10">
          <ul className="space-y-5">
            {OFFER_STACK.map((item, i) => (
              <motion.li key={item} initial={{ opacity: 0, x: -18 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }} className="flex gap-4 items-start" data-testid={`stack-item-${i + 1}`}>
                <span className="w-7 h-7 shrink-0 rounded-full bg-[#D9622B] text-black font-black text-sm flex items-center justify-center">✓</span>
                <span className="text-[15px] font-bold text-[#F0ECE4] pt-0.5">{item}</span>
              </motion.li>
            ))}
          </ul>
          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <p className="text-lg text-white/70">Your investment today: <span className="text-[#D9622B] font-black text-2xl" style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}>$0 TO ATTEND</span></p>
            <p className="mt-1 text-[13px] text-white/40">— just your time and honesty.</p>
            <button data-testid="stack-cta" onClick={() => scrollToId("book")} className="mt-6 px-8 py-4 btn-primary text-black font-black text-sm tracking-[0.1em]">
              ✓ BOOK MY FREE VA BENEFIT REVIEW
            </button>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Guarantee = () => (
  <section className="px-6 sm:px-12 lg:px-16 py-28" data-testid="guarantee-section">
    <div className="max-w-2xl mx-auto text-center">
      <SectionLabel num="003" text="THE PROMISE" />
      <Reveal delay={0.1}>
        <div className="mt-10 border-2 border-[#D9622B] p-8 sm:p-12 relative" style={{ boxShadow: "0 0 60px -20px rgba(217,98,43,0.3)" }}>
          <h2 className="text-3xl sm:text-4xl font-black uppercase text-[#D9622B]" style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}>{GUARANTEE.title}</h2>
          <p className="mt-6 text-[16px] text-white/75 leading-[1.8] italic">"{GUARANTEE.body}"</p>
          <p className="mt-6 font-bold text-[#F0ECE4]">— {HOST.name}</p>
        </div>
        <p className="mt-4 text-[11px] text-white/30">{GUARANTEE.smallPrint}</p>
      </Reveal>
    </div>
  </section>
);
